// src/components/common/SpecDiff.tsx
import React from 'react';
import { Plus, Minus, ArrowRight } from 'lucide-react';

interface SpecChange {
    key: string;
    old_value: any;
    new_value: any;
    action: 'added' | 'removed' | 'changed';
}

interface SpecDiffProps {
    specChanges: SpecChange[];
    label?: string;
    summary?: {
        added: number;
        removed: number;
        changed: number;
    };
}

const SpecDiff: React.FC<SpecDiffProps> = ({ specChanges, label = 'Specifications', summary }) => {
    if (!specChanges || specChanges.length === 0) {
        return (
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-sm text-gray-400">No specifications</p>
                </div>
            </div>
        );
    }
    
    const hasChanges = specChanges.some(s => s.action !== 'unchanged');
    const changedCount = specChanges.filter(s => s.action !== 'unchanged').length;
    
    if (!hasChanges) {
        return (
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    {specChanges.map((spec) => (
                        <div key={spec.key} className="flex justify-between py-1 text-sm border-b border-gray-100 last:border-0">
                            <span className="text-gray-600">{spec.key}</span>
                            <span className="text-gray-900">{String(spec.new_value)}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-2">
            {/* Header with label and summary */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="block text-sm font-medium text-gray-700">
                    {label}
                    {changedCount > 0 && (
                        <span className="ml-2 text-xs text-blue-600 font-normal">
                            ({changedCount} change{changedCount !== 1 ? 's' : ''})
                        </span>
                    )}
                </label>
                
                {/* Summary badges */}
                {summary && (
                    <div className="flex items-center gap-3 text-xs">
                        {summary.added > 0 && (
                            <span className="flex items-center gap-1 text-green-600">
                                <Plus className="h-3 w-3" />
                                {summary.added} added
                            </span>
                        )}
                        {summary.changed > 0 && (
                            <span className="flex items-center gap-1 text-yellow-600">
                                <ArrowRight className="h-3 w-3" />
                                {summary.changed} changed
                            </span>
                        )}
                        {summary.removed > 0 && (
                            <span className="flex items-center gap-1 text-red-600">
                                <Minus className="h-3 w-3" />
                                {summary.removed} removed
                            </span>
                        )}
                    </div>
                )}
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 text-xs">
                <span className="text-gray-500">Legend:</span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-green-100 border border-green-300 rounded-sm" />
                    <span className="text-gray-600">Added</span>
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded-sm" />
                    <span className="text-gray-600">Changed</span>
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-red-100 border border-red-300 rounded-sm" />
                    <span className="text-gray-600">Removed</span>
                </span>
            </div>
            
            {/* Spec Items - Card Layout */}
            <div className="space-y-1.5">
                {specChanges.map((spec) => {
                    const isAdded = spec.action === 'added';
                    const isRemoved = spec.action === 'removed';
                    const isModified = spec.action === 'changed';
                    
                    // Background colors for the key div
                    const keyBg = isAdded ? 'bg-green-50' : isRemoved ? 'bg-red-50' : isModified ? 'bg-yellow-50' : 'bg-gray-50';
                    const valueBg = isAdded ? 'bg-green-50' : isRemoved ? 'bg-red-50' : isModified ? 'bg-yellow-50' : 'bg-gray-50';
                    
                    // Border colors
                    const borderColor = isAdded ? 'border-green-300' : isRemoved ? 'border-red-300' : isModified ? 'border-yellow-300' : 'border-gray-200';
                    
                    return (
                        <div 
                            key={spec.key}
                            className="grid grid-cols-2 gap-1.5 rounded-lg bg-white p-1.5"
                        >
                            {/* Left: Key + Badge */}
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded border ${borderColor} ${keyBg}`}>
                                <span className="text-sm font-medium text-gray-700">{spec.key}</span>
                                {isAdded && (
                                    <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">new</span>
                                )}
                                {isRemoved && (
                                    <span className="text-xs text-red-600 bg-red-100 px-1.5 py-0.5 rounded">removed</span>
                                )}
                                {isModified && (
                                    <span className="text-xs text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded">changed</span>
                                )}
                            </div>
                            
                            {/* Right: Value(s) - Left aligned */}
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded border ${borderColor} ${valueBg}`}>
                                {isModified && (
                                    <>
                                        <span className="text-sm text-gray-400 line-through">{String(spec.old_value)}</span>
                                        <span className="text-sm text-gray-300">→</span>
                                        <span className="text-sm text-gray-900 font-medium">{String(spec.new_value)}</span>
                                    </>
                                )}
                                {isAdded && (
                                    <span className="text-sm text-green-700 font-medium">{String(spec.new_value)}</span>
                                )}
                                {isRemoved && (
                                    <span className="text-sm text-red-600 line-through">{String(spec.old_value)}</span>
                                )}
                                {!isAdded && !isRemoved && !isModified && (
                                    <span className="text-sm text-gray-700">{String(spec.new_value)}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SpecDiff;